<template>
  <v-col
    cols="12"
    class="v-treeview-node v-treeview-node--click pa-0"
    :class="{ 'v-treeview-node--leaf': hasAnyChild }"
  >
    <v-row no-gutters class="v-treeview-node__root flex-nowrap text-left pa-0" v-on="nodeListeners">
      <v-col cols="auto" class="d-flex node-levels-box">
        <div v-for="index in appendLevel" :key="index" class="v-treeview-node__level" />
      </v-col>
      <v-col cols="auto" v-if="hasAnyChild">
        <v-icon
          class="notranslate v-treeview-node__toggle v-icon--link"
          :class="{
            'v-treeview-node__toggle--open': isOpen,
          }"
        >
          {{ expandIcon }}
        </v-icon>
      </v-col>
      <v-col class="flex-grow-1">
        <v-row no-gutters align="center" class="flex-nowrap">
          <v-col cols="auto">
            <slot name="prepend" v-bind="treeviewSlotsBind">
              <slot :name="`prepend-${level}`" v-bind="treeviewSlotsBind" />
            </slot>
          </v-col>
          <v-col cols="auto" class="flex-grow-1">
            <v-row no-gutters>
              <v-col cols="auto" class="v-treeview-node__label">
                <slot name="label" v-bind="treeviewSlotsBind">
                  <slot :name="`label-${level}`" v-bind="treeviewSlotsBind">
                    {{ value[elementNameKey] }}
                  </slot>
                </slot>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="auto">
            <slot name="append" v-bind="treeviewSlotsBind">
              <slot :name="`append-${level}`" v-bind="treeviewSlotsBind" />
            </slot>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row
      no-gutters
      v-if="isOpen || (isGroupElement && !hasAnyChild && emptyGroupTranslationPath)"
      class="v-treeview-node__children v-treeview-node__children__draggable"
    >
      <draggable
        :group="draggableGroup"
        :value="value[elementChildrenKey]"
        :ghost-class="`treeview-draggable-ghost-${level + 1}`"
        class="treeview-draggable-box"
        @input="updateItems"
      >
        <treeview-draggable-node
          v-for="child in value[elementChildrenKey]"
          :key="child[elementUidKey]"
          :value="child"
          v-bind="treeviewNodeBind"
          @input="updateItem"
        >
          <template v-for="(value, key) in $scopedSlots" #[key]="scope">
            <slot :name="key" v-bind="scope" />
          </template>
        </treeview-draggable-node>
        <template v-if="isGroupEmpty">
          <slot name="empty-group-box">
            <v-row no-gutters class="treeview-draggable-no-elements-box">
              <v-col cols="auto" class="treeview-draggable-no-elements">
                <v-row no-gutters>
                  <v-col cols="auto" class="d-flex">
                    <div v-for="index in appendLevel" :key="index" class="v-treeview-node__level" />
                  </v-col>
                  <v-col cols="auto">
                    <slot name="empty-group-inner" v-bind="{ ...treeviewSlotsBind, emptyGroupTranslationPath }">
                      {{ $i18n.t(emptyGroupTranslationPath) }}
                    </slot>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </slot>
        </template>
      </draggable>
    </v-row>
  </v-col>
</template>
<script>
import TreeviewDraggableNode from '@/components/TreeviewDraggableNode';
import draggable from 'vuedraggable-multi';
export default {
  name: 'TreeviewDraggableNode',
  components: { TreeviewDraggableNode, draggable },
  props: {
    value: {
      type: Object,
      required: true,
    },
    elementChildrenKey: {
      type: String,
      default: 'children',
    },
    elementNameKey: {
      type: String,
      default: 'name',
    },
    elementUidKey: {
      type: String,
      default: 'id',
    },
    groupOpenKey: {
      type: String,
      default: null,
    },
    draggableGroup: {
      type: String,
      default: null,
    },
    emptyGroupTranslationPath: {
      type: String,
      default: 'default.noElementsInGroup',
    },
    expandIcon: {
      type: String,
      default: 'mdi-menu-down',
    },
    level: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    hasAnyChild() {
      return this.value[this.elementChildrenKey]?.length > 0;
    },
    isGroupElement() {
      return this.value[this.elementChildrenKey] ? true : false;
    },
    isGroupEmpty() {
      return this.isGroupElement && !this.hasAnyChild;
    },
    appendLevel() {
      return this.level + (this.hasAnyChild ? 0 : 1);
    },
    isOpen() {
      return this.groupOpenKey ? this.value[this.groupOpenKey] : this.open;
    },
    nodeListeners() {
      return {
        ...(this.isGroupElement && !this.isGroupEmpty
          ? {
              click: () => {
                this.toggleOpen();
              },
            }
          : {}),
      };
    },
    treeviewSlotsBind() {
      return {
        item: this.value,
        ...(this.isGroupElement ? { open: this.isOpen } : {}),
      };
    },
    treeviewNodeBind() {
      return {
        draggableGroup: this.draggableGroup,
        expandIcon: this.expandIcon,
        elementChildrenKey: this.elementChildrenKey,
        elementNameKey: this.elementNameKey,
        elementUidKey: this.elementUidKey,
        groupOpenKey: this.groupOpenKey,
        emptyGroupTranslationPath: this.emptyGroupTranslationPath,
        level: this.level + 1,
      };
    },
  },
  data: () => ({
    open: false,
  }),
  methods: {
    toggleOpen() {
      if (this.groupOpenKey) {
        this.value[this.groupOpenKey] = !this.value[this.groupOpenKey];
        this.$emit('input', this.value);
      } else {
        this.open = !this.open;
      }
    },
    updateItem(item) {
      const index = [...this.value].findIndex(v => v[this.elementUidKey] === item[this.elementUidKey]);
      this.$set(this.value, index, item);
      this.$emit('input', this.value);
    },
    updateItems(e) {
      this.$set(this.value, this.elementChildrenKey, e);
      this.$emit('input', this.value);
    },
  },
};
</script>
<style lang="scss" scoped>
.treeview-draggable-box {
  width: 100%;
  min-height: 19.5px;
  position: relative;
  .treeview-draggable-no-elements-box {
    position: absolute;
    top: 0;
    .treeview-draggable-no-elements {
      font-style: italic;
      font-size: 13px;
      opacity: 0.8;
      color: rgba(0, 0, 0, 0.6);
    }
    &:last-child:not(:first-child) {
      display: none;
    }
  }
  .sortable-chosen {
    background-color: white;
    z-index: 1;
  }
}
</style>
